import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { AuditOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment-jalaali';
import PersianDatePicker from '../../../components/PersianDatePicker';
import { attendanceApi, employeesApi } from '../../../utils/api';
import { TableSkeleton } from '../../../components/SkeletonLoaders';

const SOURCE_OPTIONS = [
  { value: 'manual_adjustment', label: 'تعدیل دستی' },
  { value: 'regularization', label: 'اصلاحیه تردد' },
  { value: 'admin_override', label: 'اعمال مدیر' },
];

const formatTime = (v) => {
  if (v == null || v === '') return '—';
  const s = String(v).trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) return s.slice(0, 5);
  const isoMatch = s.match(/T(\d{2}:\d{2})(?::\d{2})?/);
  if (isoMatch) return isoMatch[1];
  const spaceMatch = s.match(/\s(\d{2}:\d{2})(?::\d{2})?/);
  if (spaceMatch) return spaceMatch[1];
  const anyMatch = s.match(/(\d{2}:\d{2})(?::\d{2})?/);
  if (anyMatch) return anyMatch[1];
  return s;
};

const jalaliToGregorian = (jalali) => {
  if (!jalali) return '';
  const normalized = String(jalali)
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
    .replace(/-/g, '/');
  const m = moment(normalized, 'jYYYY/jMM/jDD');
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

const AttendanceAdjustmentsReport = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, per_page: 20, total: 0 });
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await employeesApi.getAll();
        const list =
          res?.data?.data ||
          res?.data?.employees ||
          res?.employees ||
          res?.data ||
          [];
        setEmployees(Array.isArray(list) ? list : []);
      } catch (e) {
        console.warn('employees for audit filter:', e?.message || e);
      }
    })();
  }, []);

  const load = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const values = form.getFieldsValue();
        const params = {
          page,
          per_page: meta.per_page || 20,
        };
        if (values.user_id) params.user_id = values.user_id;
        if (values.changed_by) params.changed_by = values.changed_by;
        if (values.source) params.source = values.source;
        if (Array.isArray(values.range) && values.range[0]) {
          params.start_date = jalaliToGregorian(values.range[0]);
        }
        if (Array.isArray(values.range) && values.range[1]) {
          params.end_date = jalaliToGregorian(values.range[1]);
        }

        const res = await attendanceApi.getAdjustmentsReport(params);
        const payload = res?.data ?? res ?? {};
        const nested = payload.data && !Array.isArray(payload.data) ? payload.data : payload;
        const list = Array.isArray(nested)
          ? nested
          : Array.isArray(nested?.data)
            ? nested.data
            : Array.isArray(nested?.adjustments)
              ? nested.adjustments
              : [];
        setRows(list);
        setMeta({
          current_page: nested?.current_page || nested?.meta?.current_page || page,
          per_page: nested?.per_page || nested?.meta?.per_page || params.per_page,
          total: nested?.total || nested?.meta?.total || list.length,
        });
      } catch (error) {
        console.error(error);
        message.error(error?.message || 'خطا در دریافت گزارش');
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [form, meta.per_page],
  );

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const employeeOptions = employees.map((e) => ({
    value: e.user_id ?? e.userId ?? e.id,
    label: e.name || e.employee_name || `#${e.id}`,
  }));

  const columns = [
    {
      title: 'تاریخ اصلاح',
      dataIndex: 'jalali_created_at',
      key: 'jalali_created_at',
      width: 140,
      render: (t, row) =>
        t || (row.created_at ? moment(row.created_at).format('jYYYY/jMM/jDD HH:mm') : '—'),
    },
    {
      title: 'کارمند',
      key: 'user',
      render: (_, row) => row.user?.name || row.employee_name || row.user_id || '—',
    },
    {
      title: 'فیلد',
      dataIndex: 'field_name_label',
      key: 'field',
      render: (t, row) =>
        t ||
        (row.field_name === 'clock_out_time'
          ? 'ساعت خروج'
          : row.field_name === 'clock_in_time'
            ? 'ساعت ورود'
            : row.field_name || '—'),
    },
    {
      title: 'قبل / بعد',
      key: 'times',
      render: (_, row) => (
        <span className="tabular-nums">
          {formatTime(row.original_time_formatted)} → {formatTime(row.adjusted_time_formatted)}
        </span>
      ),
    },
    {
      title: 'علت',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (t) => t || '—',
    },
    {
      title: 'اعمال‌کننده',
      key: 'changer',
      render: (_, row) => row.changer?.name || row.changed_by_name || row.changed_by || '—',
    },
    {
      title: 'منبع',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (s) => {
        const map = {
          manual_adjustment: { color: 'gold', label: 'تعدیل دستی' },
          regularization: { color: 'blue', label: 'اصلاحیه' },
          admin_override: { color: 'purple', label: 'مدیر' },
        };
        const m = map[s] || { color: 'default', label: s || '—' };
        return (
          <Tag color={m.color} className="custom-font m-0">
            {m.label}
          </Tag>
        );
      },
    },
  ];

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }} className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
              <AuditOutlined className="text-amber-600 text-lg" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 m-0">
                گزارش اصلاحات تردد
              </h1>
            </div>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => load(meta.current_page || 1)}
            style={{ fontFamily: 'CustomFont' }}
          >
            بارگذاری مجدد
          </Button>
        </div>

        <Card size="small" className="border-gray-100 dark:border-gray-700">
          <Form form={form} layout="vertical" onFinish={() => load(1)}>
            <Row gutter={12}>
              <Col xs={24} md={6}>
                <Form.Item name="user_id" label="کارمند" className="mb-2">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={employeeOptions}
                    placeholder="همه"
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item name="changed_by" label="اعمال‌کننده تغییر" className="mb-2">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={employeeOptions}
                    placeholder="همه"
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={5}>
                <Form.Item name="source" label="منبع" className="mb-2">
                  <Select
                    allowClear
                    options={SOURCE_OPTIONS}
                    placeholder="همه"
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={7}>
                <Form.Item name="range" label="بازه تاریخ" className="mb-2">
                  <PersianDatePicker
                    range
                    placeholder="از تاریخ — تا تاریخ"
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Space>
              <Button type="primary" htmlType="submit" style={{ fontFamily: 'CustomFont' }}>
                اعمال فیلتر
              </Button>
              <Button
                style={{ fontFamily: 'CustomFont' }}
                onClick={() => {
                  form.resetFields();
                  load(1);
                }}
              >
                پاک کردن
              </Button>
            </Space>
          </Form>
        </Card>

        <Card size="small" className="border-gray-100 dark:border-gray-700">
          {loading && rows.length === 0 ? (
            <TableSkeleton rows={8} />
          ) : (
            <Table
              rowKey={(r) => r.id || `${r.attendance_id}-${r.created_at}`}
              columns={columns}
              dataSource={rows}
              loading={loading}
              pagination={{
                current: meta.current_page,
                pageSize: meta.per_page,
                total: meta.total,
                onChange: (p) => load(p),
                showSizeChanger: false,
              }}
              scroll={{ x: 900 }}
              style={{ fontFamily: 'CustomFont' }}
              locale={{ emptyText: 'موردی یافت نشد' }}
            />
          )}
        </Card>
      </div>
    </MainLayout>
  );
};

export default AttendanceAdjustmentsReport;
